import { useState } from 'react';
import { useFadeIn } from '@/hooks/useFadeIn';

const headers = ['ICON治療', 'ラミネートベニア', 'セラミッククラウン', 'ホワイトニング'];
const rows = [
  { label: 'ホワイトスポット改善', values: ['◎', '◎', '◎', '△'] },
  { label: '歯を削る量', values: ['削らない', '少し削る', '大きく削る', '削らない'] },
  { label: '痛み', values: ['ほぼなし', '少ない', '場合による', 'なし'] },
  { label: '治療回数', values: ['1回', '2〜3回', '2〜4回', '複数回'] },
  { label: '歯への負担', values: ['非常に少ない', 'ややある', '大きい', '少ない'] },
  { label: '費用目安', values: ['33,000円〜', '100,000〜150,000円', '150,000〜250,000円', '数万円'] },
];

const mobileHeaders = ['ラミネート\nベニア', 'セラミック\nクラウン', 'ホワイト\nニング'];

export default function IconComparison() {
  const heading = useFadeIn({ direction: 'up' });
  const table = useFadeIn({ direction: 'up', delay: 200 });
  const bottom = useFadeIn({ direction: 'up', delay: 400 });
  const [openMobile, setOpenMobile] = useState<number | null>(null);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style} className="text-center mb-12 md:mb-16">
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            Treatment Comparison
          </span>
          <div className="w-8 h-px mt-3 mb-4 mx-auto" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-4xl leading-relaxed">
            ホワイトスポット治療の比較
          </h2>
          <p className="font-light text-gray-500 text-sm md:text-base mt-5 leading-loose max-w-2xl mx-auto">
            白い点の状態によって最適な治療法は異なります。<br className="hidden md:block" />
            まずは歯をできるだけ削らない治療から検討することをおすすめしています。
          </p>
        </div>

        {/* モバイル */}
        <div className="md:hidden space-y-4">
          <div
            className="rounded-xl overflow-hidden border-2"
            style={{ backgroundColor: '#EBF0F7', borderColor: '#3E5F8F' }}
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-white font-medium text-xs px-3 py-1 rounded-full tracking-wide"
                  style={{ backgroundColor: '#3E5F8F' }}
                >
                  おすすめ
                </span>
                <span className="font-medium text-gray-900 text-lg">ICON治療</span>
              </div>
              <div className="space-y-3">
                {rows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 text-sm">{row.label}</span>
                    <span className="font-medium text-sm" style={{ color: '#3E5F8F' }}>
                      {row.values[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {mobileHeaders.map((h, i) => (
            <div key={h} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                className="w-full p-4 flex items-center justify-between cursor-pointer"
                onClick={() => setOpenMobile(openMobile === i ? null : i)}
              >
                <span className="font-medium text-gray-900 text-base whitespace-pre-line leading-snug text-left">
                  {h}
                </span>
                <div className="w-6 h-6 flex items-center justify-center text-gray-400 flex-shrink-0">
                  <i
                    className="ri-arrow-down-s-line text-lg transition-transform duration-200"
                    style={{ transform: openMobile === i ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  ></i>
                </div>
              </button>
              {openMobile === i && (
                <div className="px-4 pb-4 space-y-2 border-t border-gray-100">
                  {rows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-1">
                      <span className="font-medium text-gray-700 text-sm">{row.label}</span>
                      <span className="font-light text-gray-700 text-sm">{row.values[i + 1]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* デスクトップテーブル */}
        <div ref={table.ref} style={table.style} className="hidden md:block overflow-x-auto">
          <div className="min-w-0">
            {/* ヘッダー */}
            <div className="grid grid-cols-[160px_1fr_1fr_1fr_1fr] gap-0 border-b border-gray-200">
              <div className="p-4 md:p-5 flex items-end"></div>
              {headers.map((h, i) => (
                <div
                  key={h}
                  className={`p-4 md:p-5 text-center relative ${
                    i === 0
                      ? 'rounded-t-lg border-t-2 border-x-2'
                      : ''
                  }`}
                  style={
                    i === 0
                      ? { backgroundColor: '#EBF0F7', borderColor: '#3E5F8F' }
                      : {}
                  }
                >
                  {i === 0 && (
                    <span
                      className="absolute -top-3 left-1/2 -translate-x-1/2 text-white font-medium text-[10px] px-3 py-1 rounded-full whitespace-nowrap tracking-wide"
                      style={{ backgroundColor: '#3E5F8F' }}
                    >
                      おすすめ
                    </span>
                  )}
                  <span className="font-medium text-gray-900 text-sm md:text-base whitespace-pre-line leading-snug">
                    {h.replace('ラミネートベニア', 'ラミネート\nベニア').replace('セラミッククラウン', 'セラミック\nクラウン')}
                  </span>
                </div>
              ))}
            </div>
            {/* 行 */}
            {rows.map((row, rowI) => (
              <div
                key={row.label}
                className={`grid grid-cols-[160px_1fr_1fr_1fr_1fr] gap-0 ${
                  rowI < rows.length - 1 ? 'border-b border-gray-200' : ''
                }`}
              >
                <div className="p-4 md:p-5 flex items-center">
                  <span className="font-medium text-gray-700 text-sm md:text-base">{row.label}</span>
                </div>
                {row.values.map((val, i) => (
                  <div
                    key={i}
                    className={`p-4 md:p-5 text-center flex items-center justify-center ${
                      i === 0 ? 'border-x-2' : ''
                    } ${i === 0 && rowI === rows.length - 1 ? 'border-b-2 rounded-b-lg' : ''}`}
                    style={
                      i === 0
                        ? { backgroundColor: '#EBF0F7', borderColor: '#3E5F8F' }
                        : {}
                    }
                  >
                    <span
                      className={`text-sm md:text-base leading-relaxed ${
                        i === 0 ? 'font-medium' : 'font-light text-gray-700'
                      }`}
                      style={i === 0 ? { color: '#3E5F8F' } : {}}
                    >
                      {val}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div ref={bottom.ref} style={bottom.style} className="mt-14 md:mt-16">
          <div className="bg-gray-50 rounded-lg p-8 md:p-10 max-w-3xl mx-auto">
            <h3 className="font-serif text-gray-900 text-lg md:text-xl text-center mb-5">
              まずは歯を守る治療から
            </h3>
            <div className="space-y-4">
              <p className="font-light text-gray-700 text-sm md:text-base leading-loose text-center">
                ICONは健康な歯を削らずにホワイトスポットの改善を目指せるため、近年多くの患者様に選ばれています。
              </p>
              <p className="font-light text-gray-500 text-sm md:text-base leading-loose text-center">
                ただし、白斑の深さや状態によっては他の治療法が適している場合もあります。
              </p>
              <p className="font-light text-gray-500 text-sm md:text-base leading-loose text-center">
                無料カウンセリングでは、お口の状態を確認したうえで最適な治療方法をご提案いたします。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}